import type { GoogleLocation, GoogleStatus } from "../types";
import "./review-boost-setup-guide.css";

export interface ReviewBoostSetupReadiness {
  /** The tenant-scoped /google/status GET completed with a known status. */
  connectionVerified: boolean;
  connection: GoogleStatus | null;
  /** The tenant-scoped /google/locations GET completed and returned a valid list. */
  locationsVerified: boolean;
  locations: GoogleLocation[];
}

function connectionDescription(readiness?: ReviewBoostSetupReadiness): string {
  if (!readiness?.connectionVerified || !readiness.connection) {
    return "No se pudo verificar la conexión. Revisá su estado en Configuración.";
  }
  switch (readiness.connection.status) {
    case "connected":
      return "La cuenta de Google está autorizada. Todavía hay que comprobar sus fichas.";
    case "connecting":
      return "La conexión está pendiente. Podés revisar o reintentar desde Configuración.";
    case "expired":
    case "needs_reauth":
      return "La autorización necesita renovarse para seguir operando.";
    case "error":
      return "Google devolvió un error de conexión. Revisá las opciones de recuperación.";
    case "disconnected":
      return "Todavía no conectaste Google Business Profile.";
    default:
      return "El estado de Google no está confirmado.";
  }
}

function locationDescription(readiness?: ReviewBoostSetupReadiness): string {
  if (readiness?.connection?.status !== "connected" || !readiness.connectionVerified) {
    return "Primero se necesita una conexión válida a Google.";
  }
  if (!readiness.locationsVerified) {
    return "No se pudieron consultar las fichas. No equivale a que no existan.";
  }
  if (readiness.locations.length === 0) {
    return "Cuenta autorizada, pero no se detectaron fichas en esta consulta.";
  }
  const linkedCount = readiness.locations.filter(location =>
    typeof location.location_id === "string" && location.location_id.trim().length > 0
  ).length;
  if (linkedCount === 0) {
    return `${readiness.locations.length} ${readiness.locations.length === 1 ? "ficha detectada" : "fichas detectadas"}, pero ninguna está vinculada a un local. Elegí una ficha y vinculala para continuar.`;
  }
  return `${linkedCount} ${linkedCount === 1 ? "ficha vinculada" : "fichas vinculadas"} a tu negocio. Podés revisar las ubicaciones en Configuración.`;
}

export function ReviewBoostSetupGuide({
  readiness, reviewsAvailable, base, navigate, includeAutomation = false, automationMode,
}: {
  readiness?: ReviewBoostSetupReadiness;
  reviewsAvailable: boolean;
  base: string;
  navigate: (path: string) => void;
  includeAutomation?: boolean;
  automationMode?: string;
}) {
  const connected = readiness?.connectionVerified === true
    && readiness.connection?.status === "connected";
  // A fetched but unlinked Google ficha is not a completed setup step.
  const hasLinkedLocation = connected && readiness?.locationsVerified === true
    && readiness.locations.some(location =>
      typeof location.location_id === "string" && location.location_id.trim().length > 0);
  const modeVerified = ["paused", "approval_required", "safe_auto"].includes(automationMode || "");
  const total = includeAutomation ? 5 : 4;
  const confirmed = 1 + Number(connected) + Number(hasLinkedLocation) + Number(reviewsAvailable) + Number(includeAutomation && modeVerified);
  const complete = confirmed === total;
  const googlePath = `${base}/settings/google`;
  const reviewsPath = `${base}/reviews`;

  const routeLink = (href: string, label: string) => (
    <a href={href} onClick={event => { event.preventDefault(); navigate(href); }}>
      {label} →
    </a>
  );

  return (
    <details className="rep-first-run" open={!complete}>
      <summary>
        <span>
          <strong>Puesta en marcha de Review Boost</strong>
          <small>{complete
            ? "Configuración básica verificada. Podés consultar los pasos cuando quieras."
            : "Continuá desde donde esté tu negocio. No se enviarán solicitudes automáticamente."}</small>
        </span>
        <span className="rep-first-run-progress" aria-label={`${confirmed} de ${total} comprobaciones confirmadas`}>
          {confirmed}/{total}
        </span>
      </summary>
      <ol className="rep-first-run-steps">
        <li>
          <div>
            <strong>Acceso al negocio</strong>
            <p>Tu sesión y acceso a esta organización ya fueron validados por ClickDialog.</p>
          </div>
          <span className="rep-first-run-result">Verificado</span>
        </li>
        <li>
          <div>
            <strong>Conexión con Google</strong>
            <p>{connectionDescription(readiness)}</p>
            {!connected && routeLink(googlePath, "Revisar conexión")}
          </div>
          <span className="rep-first-run-result">{connected
            ? "Verificado"
            : readiness?.connectionVerified ? "Pendiente" : "Sin verificar"}</span>
        </li>
        <li>
          <div>
            <strong>Fichas de Google</strong>
            <p>{locationDescription(readiness)}</p>
            {connected && routeLink(googlePath, "Revisar fichas")}
          </div>
          <span className="rep-first-run-result">{hasLinkedLocation
            ? "Vinculadas"
            : connected && !readiness?.locationsVerified ? "Sin verificar" : "Pendiente"}</span>
        </li>
        <li>
          <div>
            <strong>Primeras reseñas disponibles</strong>
            <p>{reviewsAvailable
              ? "Hay reseñas en el producto que podés revisar. Publicar respuestas requiere autorización vigente."
              : "Todavía no se cargaron reseñas para este negocio. Si hay fichas, revisá la sincronización en Google."}</p>
            {reviewsAvailable
              ? routeLink(reviewsPath, "Ver reseñas")
              : routeLink(googlePath, "Revisar sincronización")}
          </div>
          <span className="rep-first-run-result">{reviewsAvailable ? "Disponibles" : "Pendiente"}</span>
        </li>
      </ol>
      {includeAutomation && <section className="rbv4-setup-mode" aria-label="Modo de respuesta inicial">
        <strong>Elegí cómo querés responder</strong>
        <p>{modeVerified ? `Modo guardado: ${automationMode === "paused" ? "Pausado" : automationMode === "approval_required" ? "Borradores con aprobación" : "Automático excepto sensibles"}. No confirma permisos de publicación ni ejecución automática.` : "Todavía no pudimos verificar un modo guardado. Revisalo antes de continuar."}</p>
        {routeLink(`${base}/settings/automation`, "Elegir o revisar modo")}
      </section>}
      <p className="rep-first-run-footer">
        Esta guía sólo lee estados del servidor. Guardar un modo no autoriza respuestas automáticas.
        No necesitás configurar Grow para trabajar con las respuestas. Preparar una campaña tampoco envía mensajes.
      </p>
    </details>
  );
}
